import { getShaderNoiseTexture } from '@paper-design/shaders';
import { useState, useEffect } from 'react';

/**
 * Gates the reveal of a shader background: waits for the noise texture, and for the
 * container to have painted at least one frame at opacity 0.
 *
 * The second part matters. These backgrounds are dynamic({ssr:false}) imports, so they
 * mount well after the enter transition has already flipped `visible` to true. Without
 * a frame at the start value the container is inserted straight at its final opacity,
 * the CSS transition never runs, and the shader snaps in instead of fading.
 *
 * @returns {boolean} Whether the shader is ready to be faded in
 */
export function useShaderTextureReady() {
  const [textureReady, setTextureReady] = useState(false);
  const [painted, setPainted] = useState(false);

  useEffect(() => {
    // Two frames: the first commits opacity 0, the second gives the browser a paint
    // to transition away from.
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => setPainted(true));
    });

    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const onSettled = () => {
      if (!cancelled) setTextureReady(true);
    };

    // decode() settles once the image has loaded, or straight away if it already has.
    // Render on error too: a shader without its noise texture beats no background
    getShaderNoiseTexture().decode().then(onSettled, onSettled);

    return () => {
      cancelled = true;
    };
  }, []);

  return textureReady && painted;
}
